import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import Fade from "@mui/material/Fade";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import TablePagination from "@mui/material/TablePagination";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSnackbar } from "notistack";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import ExportGroupsDialog from "./dialog/ExportGroupsDialog";
import MbsModeDialog from "./dialog/MbsModeDialog";
import {
  getGovernmentBenefitsSummaryApi,
  exportGovernmentGroupApi,
  exportGovernmentSingleBenefitApi,
  exportGovernmentGroupSelectedApi,
} from "../../../api/government";
import { listStores } from "../../../api/stores";
import { useAuth } from "../../../context/AuthContext";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const normalizeAccessValue = (value) => String(value || "").trim().toLowerCase();

const normalizeAccessKey = (value) =>
  normalizeAccessValue(value)
    .replace(/\bphilippines\b/g, "phils")
    .replace(/\bcorporation\b/g, "corp")
    .replace(/\bincorporated\b/g, "inc")
    .replace(/[^a-z0-9]/g, "");

const accessMatches = (accessValues, candidate) => {
  const normalizedCandidate = normalizeAccessValue(candidate);
  if (!normalizedCandidate) return false;

  const candidateKey = normalizeAccessKey(candidate);
  return accessValues.some((accessValue) => {
    const accessKey = normalizeAccessKey(accessValue);
    return (
      normalizedCandidate === accessValue ||
      normalizedCandidate.includes(accessValue) ||
      accessValue.includes(normalizedCandidate) ||
      (candidateKey && accessKey && candidateKey === accessKey)
    );
  });
};

const getStoreName = (store) =>
  typeof store === "string" ? store : store?.store || store?.name || "";

const getStoreGroupName = (store) =>
  typeof store === "string" ? "" : store?.group_name || store?.group || "";

const getStoreGroupId = (store) =>
  typeof store === "string" ? "" : store?.group_id || store?.groupId || "";

const getRowStores = (row) => {
  const candidates = [
    row?.stores,
    row?.store_list,
    row?.storeList,
    row?.store_names,
    row?.storeNames,
  ];

  return candidates.find(Array.isArray) || [];
};

const GovernmentBenefits = () => {
  const [selectedMonth, setSelectedMonth] = useState(
    monthNames[new Date().getMonth()]
  );
  const [selectedYear, setSelectedYear] = useState(
    String(new Date().getFullYear())
  );
  const [selectedGroup, setSelectedGroup] = useState("");
  const [exportingGroupId, setExportingGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("group");
  const [summaryData, setSummaryData] = useState([]);
  const [storeDirectory, setStoreDirectory] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogSearch, setExportDialogSearch] = useState("");
  const [mbsModeDialogOpen, setMbsModeDialogOpen] = useState(false);
  const [pendingMbsExport, setPendingMbsExport] = useState(null);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const userPositionRaw = user?.position || "";
  const normalizedPosition = (userPositionRaw || "").toLowerCase();
  const isPrivilegedUser = user?.role === "super-admin" || user?.role === "admin";
  const storeAccessValues = useMemo(
    () =>
      Array.isArray(user?.storeAccess)
        ? user.storeAccess.map(normalizeAccessValue).filter(Boolean)
        : [],
    [user]
  );

  const currentMonthParam = useMemo(() => {
    if (!selectedYear || !selectedMonth) return "";
    const index = monthNames.indexOf(selectedMonth);
    if (index === -1) return "";
    const monthNumber = (index + 1).toString().padStart(2, "0");
    return `${selectedYear}-${monthNumber}`;
  }, [selectedMonth, selectedYear]);

  const descendingComparator = (a, b, orderByKey) => {
    if (b[orderByKey] < a[orderByKey]) return -1;
    if (b[orderByKey] > a[orderByKey]) return 1;
    return 0;
  };

  const getComparator = (orderDirection, orderByKey) =>
    orderDirection === "desc"
      ? (a, b) => descendingComparator(a, b, orderByKey)
      : (a, b) => -descendingComparator(a, b, orderByKey);

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderResult = comparator(a[0], b[0]);
      if (orderResult !== 0) return orderResult;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const handleClearFilters = () => {
    setSelectedMonth(monthNames[new Date().getMonth()]);
    setSelectedYear(String(new Date().getFullYear()));
    setSelectedGroup("");
    setSearchTerm("");
    enqueueSnackbar("Filters cleared", { variant: "info" });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (currentMonthParam) params.month = currentMonthParam;

      const [json, storesResponse] = await Promise.all([
        getGovernmentBenefitsSummaryApi(params),
        listStores().catch(() => null),
      ]);

      let apiData = json;
      if (
        json &&
        typeof json === "object" &&
        json.data &&
        typeof json.data === "object"
      ) {
        apiData = json.data;
      }

      const rawSummary = apiData.summary || apiData;
      let rows = [];

      if (Array.isArray(rawSummary)) {
        rows = rawSummary;
      } else if (rawSummary && typeof rawSummary === "object") {
        rows = Object.entries(rawSummary).map(([key, data]) => ({
          group_id: data.group_id || key,
          group: data.group_name || key,
          ...data,
        }));
      }

      setSummaryData(rows);
      setStoreDirectory(storesResponse?.data?.stores || []);
      enqueueSnackbar("Government benefits data loaded successfully", {
        variant: "success",
      });
    } catch (err) {
      const message = "Failed to load government benefits data";
      setError(message);
      setSummaryData([]);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, currentMonthParam]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSummaryData();
    setRefreshing(false);
  };

  const getRowKey = (row) => row.group_id || row.group || "";

  const toggleGroupSelection = (rowKey) => {
    setSelectedGroups((prev) =>
      prev.includes(rowKey)
        ? prev.filter((k) => k !== rowKey)
        : [...prev, rowKey]
    );
  };

  const openMbsModeDialog = (exportAction) => {
    setPendingMbsExport(() => exportAction);
    setMbsModeDialogOpen(true);
  };

  const closeMbsModeDialog = () => {
    setMbsModeDialogOpen(false);
    setPendingMbsExport(null);
  };

  const handleMbsModeSelect = async (mbsMode) => {
    const exportAction = pendingMbsExport;
    setMbsModeDialogOpen(false);
    setPendingMbsExport(null);
    if (exportAction) await exportAction(mbsMode);
  };

  const performExportSelectedGroups = async (mbsMode) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }

    if (!selectedGroups.length) {
      enqueueSnackbar("Please select at least one group to export.", {
        variant: "warning",
      });
      return;
    }

    try {
      setExportingSelected(true);
      enqueueSnackbar("Generating multi-group export...", { variant: "info" });

      const res = await exportGovernmentGroupSelectedApi({
        month: currentMonthParam,
        groupIds: selectedGroups,
        mbsMode,
      });

      const disposition = res.headers["content-disposition"];
      let filename = `government-benefits-groups-${currentMonthParam}.xlsx`;

      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = disposition.match(/filename="?(.+?)"?$/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("Exported selected groups successfully.", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("Failed to export selected groups.", {
        variant: "error",
      });
    } finally {
      setExportingSelected(false);
      setExportDialogOpen(false);
    }
  };

  const handleExportSelectedGroups = () => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }
    if (!selectedGroups.length) {
      enqueueSnackbar("Please select at least one group to export.", {
        variant: "warning",
      });
      return;
    }
    openMbsModeDialog((mbsMode) => performExportSelectedGroups(mbsMode));
  };

  const handleExport = (type) => {
    enqueueSnackbar(`Exporting government benefits report as ${type}...`, {
      variant: "info",
    });
  };

  const handleViewStores = (group) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }
    navigate(
      `/reports/government-benefits/store?group=${encodeURIComponent(
        group
      )}&month=${encodeURIComponent(currentMonthParam)}`
    );
  };

  const performExportGroup = async (groupId, groupName, mbsMode) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }

    const effectiveGroupId = groupId || groupName;

    if (!effectiveGroupId) {
      enqueueSnackbar("This group does not have a valid ID.", {
        variant: "error",
      });
      return;
    }

    console.log("Export group payload:", {
      month: currentMonthParam,
      group_id: effectiveGroupId,
      group: groupName,
    });

    try {
      setExportingGroupId(effectiveGroupId);

      const res = await exportGovernmentGroupApi({
        month: currentMonthParam,
        group_id: effectiveGroupId,
        group: groupName,
        mbsMode,
      });

      const disposition = res.headers["content-disposition"];
      let filename = `${
        groupName || effectiveGroupId
      }-government-benefits-${currentMonthParam}.xlsx`;

      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = disposition.match(/filename="?(.+?)"?$/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(`Exported group ${groupName || effectiveGroupId}`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("Failed to export group report", { variant: "error" });
    } finally {
      setExportingGroupId(null);
    }
  };

  const handleExportGroup = (groupId, groupName) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }
    if (!(groupId || groupName)) {
      enqueueSnackbar("This group does not have a valid ID.", {
        variant: "error",
      });
      return;
    }
    openMbsModeDialog((mbsMode) =>
      performExportGroup(groupId, groupName, mbsMode)
    );
  };

  const performExportFGI = async (group, mbsMode) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }

    Swal.fire({
      title: "Select Benefits to Export",
      html: `
      <style>
        .swal2-checkbox-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-top: 10px;
        }
        .swal2-checkbox-container .form-check {
          margin-bottom: 8px;
          font-size: 1.1rem;
        }
        .swal2-checkbox-container input {
          transform: scale(1.3);
          margin-right: 10px;
        }
      </style>
      <div class="swal2-checkbox-container">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="checkSss" checked>
          <label class="form-check-label" for="checkSss">SSS</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="checkPagibig" checked>
          <label class="form-check-label" for="checkPagibig">Pag-IBIG</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="checkPhic" checked>
          <label class="form-check-label" for="checkPhic">PhilHealth</label>
        </div>
      </div>
    `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Export",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const types = [];
        const checkSss = document.getElementById("checkSss");
        const checkPagibig = document.getElementById("checkPagibig");
        const checkPhic = document.getElementById("checkPhic");

        if (checkSss && checkSss.checked) types.push("sss");
        if (checkPagibig && checkPagibig.checked) types.push("pagibig");
        if (checkPhic && checkPhic.checked) types.push("phic");

        if (types.length === 0) {
          Swal.showValidationMessage(
            "Please select at least one benefit to export."
          );
        }

        return types;
      },
    }).then(async (result) => {
      if (result.isConfirmed && Array.isArray(result.value)) {
        for (const type of result.value) {
          try {
            const res = await exportGovernmentSingleBenefitApi({
              group,
              month: currentMonthParam,
              type,
              mbsMode,
            });

            const disposition = res.headers["content-disposition"];
            let filename = `${group}-${type}-${currentMonthParam}.csv`;

            if (disposition && disposition.indexOf("filename=") !== -1) {
              const matches = disposition.match(/filename="?(.+?)"?$/);
              if (matches && matches[1]) {
                filename = matches[1];
              }
            }

            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (err) {
            Swal.fire(
              "Error",
              `Could not download ${type.toUpperCase()}`,
              "error"
            );
          }
        }
      }
    });
  };

  const handleExportFGI = (group) => {
    if (!currentMonthParam) {
      enqueueSnackbar("Please select month and year first.", {
        variant: "warning",
      });
      return;
    }
    openMbsModeDialog((mbsMode) => performExportFGI(group, mbsMode));
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "number") {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value;
  };

  const accessibleGroupKeys = useMemo(() => {
    const groupNames = new Set();
    const groupIds = new Set();

    if (isPrivilegedUser) {
      return { groupNames, groupIds };
    }

    storeDirectory.forEach((store) => {
      if (!accessMatches(storeAccessValues, getStoreName(store))) return;

      const groupName = getStoreGroupName(store);
      const groupId = getStoreGroupId(store);
      if (groupName) groupNames.add(normalizeAccessKey(groupName));
      if (groupId) groupIds.add(normalizeAccessKey(groupId));
    });

    return { groupNames, groupIds };
  }, [isPrivilegedUser, storeAccessValues, storeDirectory]);

  const accessFilteredSummaryData = useMemo(() => {
    if (isPrivilegedUser) return summaryData;
    if (!storeAccessValues.length) return [];

    return summaryData.filter((item) => {
      const rowStores = getRowStores(item);
      if (
        rowStores.some((store) => accessMatches(storeAccessValues, getStoreName(store)))
      ) {
        return true;
      }

      const groupNameKey = normalizeAccessKey(item.group || item.group_name || "");
      const groupIdKey = normalizeAccessKey(item.group_id || item.groupId || "");

      return (
        (groupNameKey && accessibleGroupKeys.groupNames.has(groupNameKey)) ||
        (groupIdKey && accessibleGroupKeys.groupIds.has(groupIdKey))
      );
    });
  }, [accessibleGroupKeys, isPrivilegedUser, storeAccessValues, summaryData]);

  const positionFilteredSummaryData = useMemo(() => {
    if (!normalizedPosition.startsWith("fgi ")) {
      return accessFilteredSummaryData;
    }

    if (normalizedPosition === "fgi admin") {
      return accessFilteredSummaryData;
    }

    const brandKeyRaw = userPositionRaw.slice(4).trim();
    const brandKey = brandKeyRaw.toLowerCase();

    const is7Star =
      brandKey.includes("7star") ||
      brandKey.includes("7-star") ||
      brandKey.includes("7 star");

    const isSSJ =
      brandKey.includes("seven star") ||
      brandKey.includes("seven-star") ||
      brandKey.includes("ssj");

    const isSyzygy = brandKey.includes("syzygy");

    const isStarConcorde =
      brandKey.includes("star concorde") || brandKey.includes("concorde");

    return accessFilteredSummaryData.filter((item) => {
      const name = (item.group || "").toLowerCase();
      if (!name) return false;

      if (is7Star) {
        return (
          name.includes("7star") ||
          name.includes("7-star") ||
          name.includes("7 star")
        );
      }

      if (isSSJ) {
        return (
          name.includes("seven star") ||
          name.includes("seven-star") ||
          name.includes("ssj")
        );
      }

      if (isSyzygy) {
        return name.includes("szg");
      }

      if (isStarConcorde) {
        return name.includes("star concorde") || name.includes("concorde");
      }

      return name.includes(brandKey);
    });
  }, [accessFilteredSummaryData, normalizedPosition, userPositionRaw]);

  const groupOptions = useMemo(() => {
    const set = new Set();
    positionFilteredSummaryData.forEach((item) => {
      if (item.group) set.add(item.group);
    });
    return Array.from(set);
  }, [positionFilteredSummaryData]);

  const exportGroupOptions = useMemo(() => {
    const map = new Map();
    positionFilteredSummaryData.forEach((item) => {
      const id = item.group_id || item.group || "";
      const name = item.group || id;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, { id, name });
      }
    });
    return Array.from(map.values());
  }, [positionFilteredSummaryData]);

  const filteredBenefitsData = useMemo(
    () =>
      positionFilteredSummaryData.filter((item) => {
        const groupName = item.group || "";
        const matchesSearch =
          !searchTerm ||
          groupName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesGroup = !selectedGroup || groupName === selectedGroup;
        return matchesSearch && matchesGroup;
      }),
    [positionFilteredSummaryData, searchTerm, selectedGroup]
  );

  const sortedBenefitsData = useMemo(
    () => stableSort(filteredBenefitsData, getComparator(order, orderBy)),
    [filteredBenefitsData, order, orderBy]
  );

  const paginatedBenefitsData = useMemo(
    () =>
      sortedBenefitsData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [sortedBenefitsData, page, rowsPerPage]
  );

  const emptyTableMessage =
    !isPrivilegedUser && storeAccessValues.length === 0
      ? "No store enrolled in the account. Please contact your admin."
      : "No Data Available";

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="300px" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="200px" height={20} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[...Array(3)].map((_, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={24}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="40%" height={32} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
          <Skeleton variant="text" width="400px" height={32} sx={{ mb: 2 }} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ borderRadius: 1 }}
          />
        </Paper>
      </Box>
    );
  }

  // shared head cell style with grid lines
  const headCellBaseSx = {
    bgcolor: "#f8f9fa",
    fontWeight: "bold",
    borderBottom: "2px solid #e5e7eb",
    borderRight: "1px solid #e0e0e0",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 2,
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "#2c3e50",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AssessmentIcon sx={{ color: "#12C1DC" }} />
              Government Benefits Summary
            </Typography>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: "#12C1DC",
                  "&:hover": { bgcolor: "rgba(18, 193, 220, 0.1)" },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={20} sx={{ color: "#12C1DC" }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </motion.div>

        {/* Breadcrumbs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link underline="hover" color="inherit" href="/">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="/reports">
              Reports
            </Link>
            <Typography color="text.primary">
              Government Benefits Summary
            </Typography>
          </Breadcrumbs>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </motion.div>
        )}

        {/* Top summary card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    icon={<ReceiptIcon />}
                    label={`${filteredBenefitsData.length} Groups`}
                    color="info"
                    variant="outlined"
                  />
                  {(selectedMonth ||
                    selectedYear ||
                    selectedGroup ||
                    searchTerm) && (
                    <Chip
                      label="Filtered"
                      color="warning"
                      size="small"
                      variant="filled"
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  <Tooltip
                    title={
                      exportingSelected
                        ? "Generating multi-group export..."
                        : "Export selected groups into one Excel file"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          setSelectedGroups([]);
                          setExportDialogSearch("");
                          setExportDialogOpen(true);
                        }}
                        sx={{
                          "&:hover": { transform: "translateY(-2px)" },
                          transition: "all 0.2s",
                        }}
                      >
                        Export Group/s
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Filters card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper
            sx={{
              mb: 3,
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                p: 3,
                bgcolor: "#f8f9fa",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Filters
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search by group..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#6b7280" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      bgcolor: "white",
                      "& .MuiOutlinedInput-root": {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#12C1DC",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{ p: 3, bgcolor: "white", borderTop: "1px solid #e5e7eb" }}
            >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={selectedMonth}
                        label="Month"
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ bgcolor: "white" }}
                      >
                        {monthNames.map((m) => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={selectedYear}
                        label="Year"
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ bgcolor: "white" }}
                      >
                        <MenuItem value="2023">2023</MenuItem>
                        <MenuItem value="2024">2024</MenuItem>
                        <MenuItem value="2025">2025</MenuItem>
                        <MenuItem value="2026">2026</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={selectedGroup}
                        label="Group"
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        sx={{ bgcolor: "white" }}
                      >
                        <MenuItem value="">All Groups</MenuItem>
                        {groupOptions.map((group) => (
                          <MenuItem key={group} value={group}>
                            {group}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{
                          "&:hover": { transform: "translateY(-1px)" },
                          transition: "all 0.2s",
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
            </Box>
          </Paper>
        </motion.div>

        {/* Group summary table only (no more tabs / charts / trends) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            {/* Simple header instead of tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "#f9fafb",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <ReceiptIcon fontSize="small" />
                Group Summary
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              <TableContainer
                sx={{
                  maxHeight: "70vh",
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size={isMobile ? "small" : "medium"}
                  aria-label="government benefits summary table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 160,
                        }}
                      >
                        Actions
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          ...headCellBaseSx,
                          textAlign: "left",
                          cursor: "pointer",
                          minWidth: 180,
                        }}
                        onClick={(event) => handleRequestSort(event, "group")}
                      >
                        Group
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 120,
                        }}
                      >
                        # of Stores
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 150,
                        }}
                      >
                        1st Date Range
                      </TableCell>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 150,
                        }}
                      >
                        2nd Date Range
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={4}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 6 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <AccountBalanceIcon fontSize="small" />
                          SSS
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={6}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 6 * 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <LocalHospitalIcon fontSize="small" />
                          PhilHealth
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        colSpan={3}
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 3 * 120,
                          borderRight: "none",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <HomeIcon fontSize="small" />
                          Pag-IBIG
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {/* SSS */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      {/* <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell> */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      {/* PhilHealth */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff (ER)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (EE)
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        Total (ER)
                      </TableCell>
                      {/* Pag-IBIG */}
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        1st Cutoff
                      </TableCell>
                      <TableCell sx={{ ...headCellBaseSx, minWidth: 120 }}>
                        2nd Cutoff
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headCellBaseSx,
                          minWidth: 120,
                          borderRight: "none",
                        }}
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedBenefitsData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={20} align="center">
                          {emptyTableMessage}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <AnimatePresence>
                        {paginatedBenefitsData.map((row, index) => {
                          const isEvenRow = index % 2 === 0;
                          const groupName = row.group || "";
                          const groupId = row.group_id;
                          const storeCount =
                            row.store_count !== undefined
                              ? row.store_count
                              : row.storeCount || "-";
                          const firstDateRange =
                            row.first_date_range || row.firstDateRange || "";
                          const secondDateRange =
                            row.second_date_range || row.secondDateRange || "";
                          const sss = row.sss || {};
                          const philhealth = row.philhealth || row.phic || {};
                          const pagibig = row.pagibig || {};

                          return (
                            <motion.tr
                              key={groupName || index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{
                                duration: 0.2,
                                delay: index * 0.03,
                              }}
                              style={{
                                display: "table-row",
                                backgroundColor: isEvenRow
                                  ? "#ffffff"
                                  : "#f8f9fa",
                              }}
                              sx={{
                                "&:hover": {
                                  backgroundColor: isEvenRow
                                    ? "#f0f0f0 !important"
                                    : "#e9ecef !important",
                                  transform: "scale(1.005)",
                                  transition: "all 0.2s",
                                },
                                "& > .MuiTableCell-root": {
                                  borderRight: "1px solid #e0e0e0",
                                  borderBottom: "1px solid #e0e0e0",
                                },
                                "& > .MuiTableCell-root:last-child": {
                                  borderRight: "none",
                                },
                              }}
                            >
                              <TableCell sx={{ minWidth: 160 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  <Tooltip title="View Stores">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon />}
                                      onClick={() =>
                                        handleViewStores(groupName)
                                      }
                                      sx={{ minWidth: 110 }}
                                    >
                                      Stores
                                    </Button>
                                  </Tooltip>
                                  {(() => {
                                    const effectiveGroupId =
                                      groupId || groupName;

                                    const isExporting =
                                      exportingGroupId === effectiveGroupId;

                                    return (
                                      <Tooltip
                                        title={
                                          isExporting
                                            ? "Generating group export..."
                                            : "Export Group"
                                        }
                                      >
                                        <span>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={isExporting}
                                            onClick={() =>
                                              handleExportGroup(
                                                groupId,
                                                groupName
                                              )
                                            }
                                            sx={{ minWidth: 110 }}
                                          >
                                            {isExporting ? (
                                              <CircularProgress size={18} />
                                            ) : (
                                              <>
                                                <DownloadIcon
                                                  sx={{ mr: 0.5 }}
                                                />
                                                Group
                                              </>
                                            )}
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    );
                                  })()}
                                  {/* <Tooltip title="Export FGI">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<DownloadIcon />}
                                      onClick={() => handleExportFGI(groupName)}
                                      sx={{
                                        minWidth: 110,
                                        bgcolor: "#16a34a",
                                        "&:hover": { bgcolor: "#15803d" },
                                      }}
                                    >
                                      FGI
                                    </Button>
                                  </Tooltip> */}
                                </Box>
                              </TableCell>

                              <TableCell
                                sx={{ minWidth: 180, whiteSpace: "nowrap" }}
                              >
                                {groupName}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 120 }}>
                                {storeCount}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 150 }}>
                                {firstDateRange}
                              </TableCell>
                              <TableCell align="center" sx={{ minWidth: 150 }}>
                                {secondDateRange}
                              </TableCell>
                              {/* SSS */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff1_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2)}
                              </TableCell>
                              {/* <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.cutoff2_er)}
                              </TableCell> */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(sss.overall_er)}
                              </TableCell>
                              {/* PhilHealth */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.cutoff1_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.cutoff2_er)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.overall)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(philhealth.overall_er)}
                              </TableCell>
                              {/* Pag-IBIG */}
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pagibig.cutoff1)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pagibig.cutoff2)}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 120 }}>
                                {formatNumber(pagibig.overall)}
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredBenefitsData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #e5e7eb",
                  "& .MuiTablePagination-actions": {
                    color: "#12C1DC",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "#12C1DC",
                  },
                }}
              />
            </Box>

            {/* Footer note */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <Typography variant="body2">
                {currentMonthParam
                  ? `Showing data for ${selectedMonth} ${selectedYear}`
                  : "Showing data for all periods"}
              </Typography>
            </Box>
          </Paper>
        </motion.div>
        <ExportGroupsDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          options={exportGroupOptions}
          selectedGroups={selectedGroups}
          onToggleGroup={toggleGroupSelection}
          searchValue={exportDialogSearch}
          onSearchChange={setExportDialogSearch}
          onExport={handleExportSelectedGroups}
          exporting={exportingSelected}
        />
        <MbsModeDialog
          open={mbsModeDialogOpen}
          onClose={closeMbsModeDialog}
          onSelect={handleMbsModeSelect}
        />
      </Box>
    </Fade>
  );
};

export default GovernmentBenefits;
